import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Search, Users, Upload, Building2, Loader2, CheckCircle2, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const CLIENT_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "sole_trader", label: "Sole trader" },
  { value: "ltd", label: "Ltd company" },
  { value: "partnership", label: "Partnership" },
];

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", business_name: "", client_code: "", client_type: "individual", uk_utr: "", companies_house_no: "",
    year_end_date: "", confirmation_statement_date: "", ch_auth_code: "",
    payroll_paye_reference: "", payroll_accounts_office_reference: "",
    gg_user_id: "", gg_password: "",
    vat_registered: false, vat_period_end_month: "",
    aml_check_completed: false,
    status: "active", drive_folder_url: "",
  });
  // Companies House search state — only used inside the New client dialog.
  const [chQuery, setChQuery] = useState("");
  const [chResults, setChResults] = useState([]);
  const [chSearching, setChSearching] = useState(false);
  const [chLoadingNumber, setChLoadingNumber] = useState(null);
  const [chFilledFrom, setChFilledFrom] = useState(null); // company_number we last auto-filled from

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllOnPage = () => setSelected((s) => {
    const allIds = rows.map((c) => c.client_id);
    const allSelected = allIds.every((id) => s.has(id));
    if (allSelected) {
      const n = new Set(s); allIds.forEach((id) => n.delete(id)); return n;
    }
    const n = new Set(s); allIds.forEach((id) => n.add(id)); return n;
  });
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post("/clients/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} client${r.data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  useEffect(() => {
    // Debounced typeahead — only runs while the dialog is open.
    if (!open || chQuery.trim().length < 2) { setChResults([]); return; }
    const q = chQuery.trim();
    setChSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/companies-house/search", { params: { q } });
        setChResults(r.data.items || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Companies House search failed");
        setChResults([]);
      } finally {
        setChSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [chQuery, open]);

  const applyCompaniesHouseProfile = async (companyNumber) => {
    setChLoadingNumber(companyNumber);
    try {
      const r = await api.get(`/companies-house/company/${companyNumber}`);
      const p = r.data;
      // Build the year-end date. Prefer the *next* accounts made-up-to (period end),
      // otherwise construct from the ARD day/month in the current year.
      let yearEnd = p.accounts_next_made_up_to || "";
      if (!yearEnd && p.accounting_reference_day && p.accounting_reference_month) {
        const y = new Date().getFullYear();
        const mm = String(p.accounting_reference_month).padStart(2, "0");
        const dd = String(p.accounting_reference_day).padStart(2, "0");
        yearEnd = `${y}-${mm}-${dd}`;
      }
      setForm((f) => ({
        ...f,
        name: p.company_name || f.name,
        business_name: p.company_name || f.business_name,
        client_type: "ltd",
        companies_house_no: p.company_number || f.companies_house_no,
        year_end_date: yearEnd || f.year_end_date,
        confirmation_statement_date: p.confirmation_statement_next_due || p.confirmation_statement_next_made_up_to || f.confirmation_statement_date,
      }));
      setChFilledFrom(p.company_number);
      toast.success(`Pulled ${p.company_name} — check the dates and save`);
      setChResults([]);
      setChQuery("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load company");
    } finally {
      setChLoadingNumber(null);
    }
  };

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    const r = await api.get("/clients", { params });
    setRows(r.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.vat_period_end_month = payload.vat_period_end_month ? parseInt(payload.vat_period_end_month, 10) : null;
      await api.post("/clients", payload);
      toast.success("Client created");
      setOpen(false);
      setForm({ name: "", business_name: "", client_code: "", client_type: "individual", uk_utr: "", companies_house_no: "", year_end_date: "", confirmation_statement_date: "", ch_auth_code: "", payroll_paye_reference: "", payroll_accounts_office_reference: "", gg_user_id: "", gg_password: "", vat_registered: false, vat_period_end_month: "", aml_check_completed: false, status: "active", drive_folder_url: "" });
      setChQuery(""); setChResults([]); setChFilledFrom(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitCsv = async (e) => {
    e.preventDefault();
    if (!importFile) return toast.error("Choose a CSV file first");
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await fetch(`${API}/clients/import-csv`, { method: "POST", credentials: "include", body: fd });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setImportResult(data);
      toast.success(`${data.created_count} client(s) imported`);
      load();
    } catch (err) { toast.error(err.message?.slice(0, 200) || "Import failed"); }
    finally { setImportBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/clients/import-template");
      const header = r.data.headers.join(",");
      const sample = r.data.headers.map((h) => JSON.stringify(r.data.sample_row[h] ?? "")).join(",");
      const blob = new Blob([header + "\n" + sample + "\n"], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "taxegy-client-import-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to load template"); }
  };

  return (
    <div>
      <PageHeader
        testid="page-clients"
        title="Clients"
        subtitle={`${rows.length} matching`}
        actions={
          <div className="flex items-center gap-2">
            <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportFile(null); setImportResult(null); } }}>
              <DialogTrigger asChild>
                <Button data-testid="import-csv-btn" variant="outline" className="rounded-md">
                  <Upload className="h-4 w-4 mr-2" /> Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="import-csv-dialog" className="max-w-lg">
                <DialogHeader><DialogTitle>Import clients from CSV</DialogTitle></DialogHeader>
                <form onSubmit={submitCsv} className="space-y-3 text-sm">
                  <div className="text-slate-600">
                    Upload a CSV to bulk-add clients. Rows with a name that already exists are skipped.
                    <button type="button" onClick={downloadTemplate} className="ml-2 text-blue-600 hover:underline" data-testid="download-template-btn">Download template</button>
                  </div>
                  <div>
                    <Label>CSV file</Label>
                    <Input data-testid="csv-file-input" type="file" accept=".csv,text/csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
                  </div>
                  {importResult && (
                    <div className="border border-slate-200 rounded-md p-3 bg-slate-50 text-xs space-y-1">
                      <div>Created: <span className="font-mono">{importResult.created_count}</span></div>
                      {importResult.skipped_duplicates?.length > 0 && (
                        <div>Skipped duplicates: <span className="font-mono">{importResult.skipped_duplicates.length}</span>
                          <div className="text-slate-500 mt-1">{importResult.skipped_duplicates.slice(0, 5).join(", ")}{importResult.skipped_duplicates.length > 5 ? "…" : ""}</div>
                        </div>
                      )}
                      {importResult.errors?.length > 0 && (
                        <div className="text-rose-700">
                          Errors: {importResult.errors.length}
                          <ul className="mt-1 list-disc pl-5 text-slate-700">
                            {importResult.errors.slice(0, 5).map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <DialogFooter>
                    <Button data-testid="submit-csv-btn" type="submit" disabled={importBusy || !importFile} className="bg-slate-900 hover:bg-slate-800">
                      {importBusy ? "Importing…" : "Import"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-client-btn" className="rounded-md bg-slate-900 hover:bg-slate-800">
                  <Plus className="h-4 w-4 mr-2" /> New client
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="client-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New client</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                {/* Companies House lookup — auto-fills the form for UK Ltd companies */}
                <div className="rounded-md border border-slate-200 bg-slate-50/70 p-3 space-y-2" data-testid="ch-lookup-block">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Building2 className="h-4 w-4" />
                    <span className="text-sm font-medium">Look up on Companies House</span>
                  </div>
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      data-testid="ch-search-input"
                      className="pl-9"
                      placeholder="Type a company name or number…"
                      value={chQuery}
                      onChange={(e) => setChQuery(e.target.value)}
                    />
                    {chSearching && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                  </div>
                  {chResults.length > 0 && (
                    <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100" data-testid="ch-results-list">
                      {chResults.map((c) => (
                        <button
                          type="button"
                          key={c.company_number}
                          data-testid={`ch-result-${c.company_number}`}
                          onClick={() => applyCompaniesHouseProfile(c.company_number)}
                          disabled={chLoadingNumber === c.company_number}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 disabled:opacity-60"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{c.company_name}</div>
                            <div className="text-[11px] text-slate-500 truncate">
                              #{c.company_number} · {c.company_status || "—"} · {c.address || "no address"}
                            </div>
                          </div>
                          {chLoadingNumber === c.company_number ? (
                            <Loader2 className="h-4 w-4 text-slate-400 animate-spin flex-shrink-0" />
                          ) : (
                            <span className="text-[11px] text-slate-400 flex-shrink-0">Use →</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {chFilledFrom && (
                    <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="ch-filled-indicator">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Filled from Companies House #{chFilledFrom} — double-check the dates before saving.
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-[1fr,1fr,140px] gap-3">
                  <div>
                    <Label>Client name *</Label>
                    <Input
                      data-testid="client-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Business name</Label>
                    <Input
                      data-testid="client-business-name-input"
                      value={form.business_name}
                      onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Client code</Label>
                    <Input
                      data-testid="client-code-input"
                      placeholder="e.g. MAT001"
                      value={form.client_code}
                      onChange={(e) => setForm({ ...form, client_code: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.client_type} onValueChange={(v) => setForm({ ...form, client_type: v })}>
                      <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CLIENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>UK UTR (stored, redacted in logs)</Label>
                    <Input
                      value={form.uk_utr}
                      onChange={(e) => setForm({ ...form, uk_utr: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Companies House no.</Label>
                    <Input
                      value={form.companies_house_no}
                      onChange={(e) => setForm({ ...form, companies_house_no: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Year-end date</Label>
                    <Input
                      type="date"
                      data-testid="client-year-end-input"
                      value={form.year_end_date}
                      onChange={(e) => setForm({ ...form, year_end_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Confirmation statement date</Label>
                    <Input
                      type="date"
                      data-testid="client-cs-date-input"
                      value={form.confirmation_statement_date}
                      onChange={(e) => setForm({ ...form, confirmation_statement_date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Companies House authentication code <span className="text-rose-600">· sensitive</span></Label>
                  <Input
                    type="password"
                    autoComplete="off"
                    placeholder="6-character CH auth code"
                    data-testid="client-ch-code-input"
                    value={form.ch_auth_code}
                    onChange={(e) => setForm({ ...form, ch_auth_code: e.target.value })}
                  />
                  <div className="text-[11px] text-slate-500 mt-1">Stored securely; masked by default; never included in emails.</div>
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <label className="inline-flex items-center gap-2 text-sm py-2">
                    <input
                      type="checkbox"
                      data-testid="client-vat-toggle"
                      checked={form.vat_registered}
                      onChange={(e) => setForm({ ...form, vat_registered: e.target.checked, vat_period_end_month: e.target.checked ? form.vat_period_end_month : "" })}
                    />
                    VAT-registered
                  </label>
                  <div>
                    <Label>VAT quarter-end month</Label>
                    <Select
                      value={form.vat_period_end_month?.toString() || ""}
                      onValueChange={(v) => setForm({ ...form, vat_period_end_month: v })}
                      disabled={!form.vat_registered}
                    >
                      <SelectTrigger data-testid="client-vat-month-select"><SelectValue placeholder="Pick a month…" /></SelectTrigger>
                      <SelectContent>
                        {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                          <SelectItem key={m} value={(i + 1).toString()}>{m} (quarters end {m}/{["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"][i]}/etc.)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Google Drive folder URL</Label>
                  <Input
                    placeholder="https://drive.google.com/..."
                    value={form.drive_folder_url}
                    onChange={(e) => setForm({ ...form, drive_folder_url: e.target.value })}
                  />
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Payroll (PAYE) — optional</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Employer PAYE reference</Label>
                      <Input
                        data-testid="new-client-paye-ref"
                        placeholder="e.g. 123/AB456"
                        value={form.payroll_paye_reference}
                        onChange={(e) => setForm({ ...form, payroll_paye_reference: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Accounts Office reference</Label>
                      <Input
                        data-testid="new-client-accounts-office-ref"
                        placeholder="e.g. 123PA00012345"
                        value={form.payroll_accounts_office_reference}
                        onChange={(e) => setForm({ ...form, payroll_accounts_office_reference: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Government Gateway — optional</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Gateway user ID</Label>
                      <Input
                        data-testid="new-client-gg-user-id"
                        placeholder="12-digit ID"
                        value={form.gg_user_id}
                        onChange={(e) => setForm({ ...form, gg_user_id: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Gateway password</Label>
                      <Input
                        data-testid="new-client-gg-password"
                        type="password"
                        autoComplete="new-password"
                        value={form.gg_password}
                        onChange={(e) => setForm({ ...form, gg_password: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">Encrypted at rest · reveal on client overview is audit-logged.</div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      data-testid="new-client-aml-toggle"
                      checked={!!form.aml_check_completed}
                      onCheckedChange={(v) => setForm({ ...form, aml_check_completed: !!v })}
                    />
                    <span className="text-sm text-slate-800 font-medium">AML / KYC checks completed</span>
                  </label>
                  <div className="text-[11px] text-slate-500 mt-1">Tick if you have already completed anti-money-laundering / know-your-customer checks for this client. You can also update this later on the client overview.</div>
                </div>
                <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t border-slate-200">
                  <Button data-testid="save-client-btn" type="submit" className="bg-slate-900 hover:bg-slate-800">Create client</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="clients-search"
              placeholder="Search name or business…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-9 w-80 bg-white"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="clients-status-filter" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-md">Apply</Button>
        </div>

        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {selected.size > 0 && (
            <div
              data-testid="clients-bulk-bar"
              className="bg-slate-900 text-slate-100 px-4 py-2 flex items-center justify-between text-sm"
            >
              <span data-testid="clients-selected-count">
                <span className="font-semibold">{selected.size}</span> selected
              </span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={clearSelection} className="text-slate-300 hover:text-white text-xs" data-testid="clients-clear-selection">
                  Clear
                </button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="h-8"
                  data-testid="clients-bulk-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
                <tr>
                  <th className="w-10 px-4 py-2">
                    <Checkbox
                      data-testid="clients-select-all"
                      checked={rows.length > 0 && rows.every((c) => selected.has(c.client_id))}
                      onCheckedChange={toggleAllOnPage}
                      aria-label="Select all clients on this page"
                    />
                  </th>
                  <th className="text-left px-4 py-2 font-semibold">Name</th>
                  <th className="text-left px-4 py-2 font-semibold">Business</th>
                  <th className="text-left px-4 py-2 font-semibold">Code</th>
                  <th className="text-left px-4 py-2 font-semibold">Type</th>
                  <th className="text-left px-4 py-2 font-semibold">UTR</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center text-slate-500">
                      <Users className="h-6 w-6 mx-auto text-slate-300 mb-2" />
                      No clients yet — create your first one.
                    </td>
                  </tr>
                )}
                {rows.map((c) => (
                  <tr key={c.client_id} data-testid="client-row" className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(c.client_id) ? "bg-slate-50" : ""}`}>
                    <td className="px-4 py-3">
                      <Checkbox
                        data-testid={`client-row-select-${c.client_id}`}
                        checked={selected.has(c.client_id)}
                        onCheckedChange={() => toggleOne(c.client_id)}
                        aria-label={`Select ${c.name}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/clients/${c.client_id}`} className="text-slate-900 font-medium hover:text-blue-600 transition-colors">
                        {c.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.business_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500" data-testid={`client-row-code-${c.client_id}`}>{c.client_code || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.client_type.replace("_", " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{c.uk_utr_redacted || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="clients-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} client{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This also removes every contact, job, service, reminder and note attached to
              {selected.size === 1 ? " this client" : " these clients"}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="clients-bulk-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {bulkDeleting ? "Deleting…" : `Yes, delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
